import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { AppState } from 'projects/ngx-messaging/src/app.reducer';
import { setLoadingResolve } from 'projects/ngx-messaging/src/modules/shared/store/ui.actions';
import { AuthenticationService } from 'projects/ngx-messaging/src/modules/user/services/authentication.service';
import { environment } from 'src/environments/environment';

import { Store } from '@ngrx/store';

export interface ILoginData {
  username: string;
  password: string;
  dealerId: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private store = inject(Store<AppState>);
  private auth = inject(AuthenticationService);
  private location = inject(Location);
  private router = inject(Router);

  @Input() readonly dealerId!: string;
  @Input() readonly redirect!: boolean;

  loginForm!: FormGroup;
  hide = signal(true);
  isLoading = signal(false);
  loginError = signal(false);

  readonly forgotPasswordUrl = environment.forgotPasswordLink;

  constructor() {
    this.store.dispatch(setLoadingResolve({ value: false }));
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      dealerId: [this.dealerId, Validators.required],
      username: [null, Validators.required],
      password: [null, Validators.required],
    });
  }

  login(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.loginError.set(false);

    this.auth
      .login(this.loginForm.value as ILoginData)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.auth.setTokenAndDealerId(response.token, response.dealerId);
          if (this.redirect) {
            this.location.back();
          } else {
            this.router.navigate(['']);
          }
        },
        error: () => {
          this.loginError.set(true);
        },
      });
  }

  togglePasswordVisibility(): void {
    this.hide.update((value) => !value);
  }
}
